import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * WHEELS UP — easter egg #1, the automatic kind.
 *
 * The instant a board crosses to 100% Done — the last open card moves across and
 * every card on the board is complete — a brief, tasteful moment plays over the
 * real board: a runway-lights sweep, a restrained vapour burst, and a quiet
 * "Wheels up" toast on the same surface as every confirmation. A HAPPY ACCIDENT:
 * it rewards the act of FINISHING, not the act of looking at an already-finished
 * board, so it never fires on load — only on the transition.
 *
 * This component is a pure, READ-ONLY consumer of a completeness signal the board
 * already computes. It owns no board state and never touches the epic roll-up or
 * the progress computation. Everything it draws is an overlay with
 * pointer-events:none, so the board stays fully usable while it plays. Motion is
 * GPU-only (transform/opacity), on the app's own --curve-out curve, and
 * prefers-reduced-motion collapses it to the toast alone. Desktop only.
 *
 * Built from the approved mock dev-preview/easter-eggs/wheels-up/ — the drawn
 * takeoff mark (house line-art, not the ✈ emoji), the deterministic burst
 * layout, the R11 palette (brand teal + done-green + soft vapour-white only) and
 * the timing are lifted from it. The static mock froze a travelling bloom at one
 * instant; here the sweep is expressed as a travelling bloom plus a wave of
 * lights igniting left→right, both by transform only (the mock's own R7 rule
 * forbids animating left/top/width/height).
 */

/* The "wheels up" mark — a clean takeoff angled up-right, drawn in the app's
   glyph family (24×24, 1.8 stroke, round joins). Lifted exactly from the mock. */
const PLANE = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.5 3.2 2.8 10.1a.6.6 0 0 0 .05 1.13l7.07 2.07 2.07 7.07a.6.6 0 0 0 1.13.05z" />
    <path d="M21.5 3.2 9.9 13.3" />
  </svg>
);

// Evenly-spaced approach lights that ignite left→right as the bloom passes.
const RUNWAY_LIGHTS = 26;

// The vapour/confetti burst — a low-count fan from top-centre. Teal, done-green
// and soft vapour-white only; no red or amber (those stay semantic, R11). Each
// tuple is the approved mock's deterministic layout: [x%, finalY px, kind, tone,
// size px, rotation deg, peak opacity].
type Tone = 't' | 'g' | 'v';
type Part = { x: number; ty: number; kind: 'puff' | 'fleck'; tone: Tone; s: number; rot: number; op: number };
const BURST: Part[] = [
  { x: 50, ty: 6, kind: 'puff', tone: 'v', s: 26, rot: 0, op: 0.5 },
  { x: 43, ty: 30, kind: 'fleck', tone: 't', s: 7, rot: -24, op: 0.95 },
  { x: 57, ty: 26, kind: 'fleck', tone: 'g', s: 7, rot: 30, op: 0.95 },
  { x: 49, ty: 52, kind: 'fleck', tone: 'v', s: 6, rot: 12, op: 0.85 },
  { x: 38, ty: 58, kind: 'fleck', tone: 'g', s: 7, rot: -40, op: 0.8 },
  { x: 62, ty: 60, kind: 'fleck', tone: 't', s: 7, rot: 48, op: 0.8 },
  { x: 33, ty: 92, kind: 'fleck', tone: 't', s: 6, rot: 18, op: 0.62 },
  { x: 67, ty: 96, kind: 'fleck', tone: 'g', s: 6, rot: -22, op: 0.6 },
  { x: 46, ty: 104, kind: 'puff', tone: 'v', s: 18, rot: 0, op: 0.32 },
  { x: 55, ty: 120, kind: 'fleck', tone: 'v', s: 6, rot: 36, op: 0.5 },
  { x: 29, ty: 128, kind: 'fleck', tone: 'g', s: 6, rot: -14, op: 0.42 },
  { x: 72, ty: 132, kind: 'fleck', tone: 't', s: 6, rot: 26, op: 0.42 },
  { x: 41, ty: 150, kind: 'fleck', tone: 't', s: 5, rot: -30, op: 0.32 },
  { x: 60, ty: 158, kind: 'fleck', tone: 'g', s: 5, rot: 20, op: 0.3 },
  { x: 50, ty: 150, kind: 'puff', tone: 'v', s: 14, rot: 0, op: 0.2 },
  { x: 24, ty: 168, kind: 'fleck', tone: 'v', s: 5, rot: 10, op: 0.24 },
  { x: 77, ty: 174, kind: 'fleck', tone: 't', s: 5, rot: -18, op: 0.22 },
  { x: 52, ty: 186, kind: 'fleck', tone: 'g', s: 5, rot: 14, op: 0.16 },
];
const TONE: Record<Tone, string> = {
  g: 'var(--s-done)',
  v: 'color-mix(in srgb,var(--t1) 12%,#ffffff)',
  t: 'var(--brand)',
};

/*
 * The trigger decision, as a pure function so it can be reasoned about and
 * tested on its own. Given the previous completeness (`null` before the first
 * observation), the current completeness, the signature already celebrated for
 * this board, and the current completed-set signature, it says what to do:
 *   'none'  — a mount baseline, no change, or this exact completion already played
 *   'clear' — the board fell back below 100%, so end any moment in flight
 *   'play'  — a genuine crossing into a not-yet-celebrated 100%
 * The mount baseline (prev === null) is what keeps an already-finished board
 * from celebrating on load: the egg rewards finishing, not looking.
 */
export function wheelsUpAction(
  prev: boolean | null,
  complete: boolean,
  seenSignature: string,
  signature: string,
): 'none' | 'clear' | 'play' {
  if (prev === null || prev === complete) return 'none';
  if (!complete) return 'clear';
  if (seenSignature === signature) return 'none';
  return 'play';
}

type Props = {
  /** Whether every card on the board is Done right now (read-only, from Board). */
  complete: boolean;
  /** A stable signature of the completed set, so the same completion plays once. */
  signature: string;
  /** The board's id, so the one-time guard is per board. */
  boardId: string;
  /** The board's name, for the toast's second line. */
  boardName?: string;
};

export default function WheelsUp({ complete, signature, boardId, boardName }: Props) {
  const [playing, setPlaying] = useState(false);
  // The previous completeness. `null` until the first observation, which only
  // establishes a baseline — a board already finished on load never celebrates.
  const was = useRef<boolean | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const start = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setPlaying(true);
    // The toast lingers ~4.5s, then the whole moment clears. The overlay's own
    // animations finish well before this and fade themselves to nothing.
    timer.current = setTimeout(() => setPlaying(false), 4500);
  }, []);
  const stop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setPlaying(false);
  }, []);

  useEffect(() => {
    const prev = was.current;
    was.current = complete;
    const key = `flightdeck-wheels-up-${boardId}`;
    let seen = '';
    try {
      seen = localStorage.getItem(key) ?? '';
    } catch {
      /* private browsing — the moment still plays, it just isn't remembered */
    }
    // Play once per distinct completed set: the signature flag blocks a repeat
    // when the same cards are re-completed, and the mount baseline (inside
    // wheelsUpAction) already blocks a repeat on reload.
    const action = wheelsUpAction(prev, complete, seen, signature);
    if (action === 'clear') {
      stop(); // fell back below 100% — clear any moment in flight
      return;
    }
    if (action !== 'play') return;
    try {
      localStorage.setItem(key, signature);
    } catch {
      /* ignore — see above */
    }
    start();
  }, [complete, signature, boardId, start, stop]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Dismiss on Escape while the moment is up (click dismiss is on the toast).
  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing, stop]);

  if (!playing) return null;

  const reduced =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      {!reduced && (
        <div className="wu-overlay" aria-hidden="true">
          <div className="wu-runway">
            <div className="wu-track" />
            <div className="wu-bloom" />
            {Array.from({ length: RUNWAY_LIGHTS }, (_, i) => {
              const f = (i + 0.5) / RUNWAY_LIGHTS;
              return (
                <span
                  key={i}
                  className="wu-light"
                  style={{ left: `${(f * 100).toFixed(2)}%`, animationDelay: `${(f * 2.2).toFixed(2)}s` }}
                />
              );
            })}
          </div>
          <div className="wu-burst">
            {BURST.map((p, i) => (
              <span
                key={i}
                className={p.kind === 'puff' ? 'wu-puff' : 'wu-fleck'}
                style={
                  {
                    left: '50%',
                    width: `${p.s}px`,
                    height: p.kind === 'puff' ? `${p.s}px` : `${(p.s * 0.5).toFixed(1)}px`,
                    background: TONE[p.tone],
                    animationDelay: `${(0.04 * (i % 7)).toFixed(2)}s`,
                    '--wu-dx': p.x - 50,
                    '--wu-ty': `${p.ty}px`,
                    '--wu-rot': `${p.rot}deg`,
                    '--wu-op': p.op,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
        </div>
      )}
      <div
        className={`wu-toast${reduced ? ' wu-static' : ''}`}
        role="status"
        aria-live="polite"
        onClick={stop}
      >
        <span className="wu-toast-mark">{PLANE}</span>
        <div className="wu-toast-main">
          <span className="wu-toast-title">Wheels up</span>
          <span className="wu-toast-sub">
            {boardName ? `${boardName} — every card is done.` : 'Every card on the board is done.'}
          </span>
        </div>
      </div>
    </>
  );
}
